import { Heading, Wrap, WrapItem } from '@chakra-ui/react';
import { env } from 'features/common/config/envConfig';
import { useCurrentOrgRegion } from 'features/common/components/OrganizationAppAccess/utils';
import { useMainStateDispatch } from 'features/main/context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { iconByProduct } from 'features/subscriptions/utils';
import {
  useGetCountOfProductQuery,
  useGetOrderActivationsQuery,
  useGetTenantsQuery,
  useGetUserOrgDepsQuery,
} from 'gql/graphql';
import { EnvironmentFlags, useFeature } from 'lib/featureFlags/features';
import { useMemo } from 'react';
import TryAppCard from './TryAppCard';
import { AppType } from './types';
import {
  hasStreamTenant,
  isCustomerOrganization,
  regionValidatorPerProduct,
} from './utils';

const descriptionByProduct = {
  Connect:
    'Try integrating Sitecore products with thousands of applications for free.',
  ai: 'Agentic workflows and agents',
};

const productMapping = {
  Connect: 'connect',
  ai: 'stream',
};

const featureFlagMapping = {
  Connect: 'PROVISION_APPS',
  ai: 'PROVISION_STREAM',
};

const useTryApp = (productCode: AppType) => {
  const featureFlagName = featureFlagMapping[productCode];
  const isFeatureFlagEnabled = useFeature(
    featureFlagName as keyof EnvironmentFlags,
  );

  const {
    data,
    loading: productCountLoading,
    error,
  } = useGetCountOfProductQuery({
    variables: {
      productCode,
    },
    skip: productCode !== 'ai' && !isFeatureFlagEnabled,
  });

  const hasApp = useMemo(
    () => (data?.organization?.applications?.totalCount ?? 0) > 0,
    [data?.organization?.applications?.totalCount],
  );

  const {
    data: activationsOrders,
    loading: activationsOrdersLoading,
    error: activationsOrderError,
  } = useGetOrderActivationsQuery({
    variables: {
      productCode: productMapping[productCode],
    },
    skip: productCode !== 'ai' && !isFeatureFlagEnabled,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const orgRegion = useCurrentOrgRegion();
  const validator = regionValidatorPerProduct[productCode];
  const isValidRegion = validator(orgRegion);

  const appNotTried =
    !activationsOrderError &&
    !error &&
    !hasApp &&
    isValidRegion &&
    isFeatureFlagEnabled;

  const isPendingActivation = useMemo(
    () =>
      !!activationsOrders?.serviceNow.activationOrder?.activationItems.length,
    [activationsOrders?.serviceNow.activationOrder?.activationItems.length],
  );

  const isLoading = useMemo(
    () => activationsOrdersLoading || productCountLoading,
    [activationsOrdersLoading, productCountLoading],
  );

  return useMemo(
    () => [
      productCode,
      isPendingActivation,
      appNotTried,
      isLoading,
      error,
      isValidRegion,
    ],
    [
      productCode,
      isPendingActivation,
      appNotTried,
      error,
      isLoading,
      isValidRegion,
    ],
  );
};

const TryApp = ({
  productCode,
  showApp,
  isLoading,
  isPendingActivation,
}: {
  productCode: AppType;
  showApp: boolean;
  isLoading: boolean;
  isPendingActivation: boolean;
}) => {
  const product = iconByProduct[productCode.toLowerCase()];
  const description = descriptionByProduct[productCode];
  const [, dispatch] = useMainStateDispatch();

  if (!showApp) return null;
  return (
    <TryAppCard
      icon={product.iconUrl}
      name={product.name}
      description={description}
      isLoading={isLoading}
      isPendingActivation={isPendingActivation}
      //@ts-ignore
      onClick={() => dispatch(openDrawer(`try-${productCode.toLowerCase()}`))}
    />
  );
};

const TryApps = () => {
  const {
    data: orgDepsData,
    loading: orgDepsLoading,
    error: orgDepsError,
  } = useGetUserOrgDepsQuery();
  const {
    data: tenantsData,
    loading: tenantsLoading,
    error: tenantsError,
  } = useGetTenantsQuery({
    variables: {
      first: 100,
      env,
    },
    nextFetchPolicy: 'cache-only',
  });

  if (orgDepsError || tenantsError) {
    console.error('[TryApps] Query failed:', { orgDepsError, tenantsError });
  }

  const [
    connect,
    isConnectPendingActivation,
    connectNotTried,
    isConnectLoading,
  ] = useTryApp('Connect');

  const [
    stream,
    isStreamPendingActivation,
    ,
    isStreamLoading,
    ,
    isStreamRegionValid,
  ] = useTryApp('ai');

  const isCustomerOrg = isCustomerOrganization(orgDepsData?.organization?.type);
  const orgId = orgDepsData?.organization?.id;
  const hasExistingStreamTenant = orgId
    ? hasStreamTenant(
        (tenantsData?.user?.applications?.nodes ?? []).filter(
          (node) => node.organizationId === orgId,
        ),
      )
    : false;
  const canStreamShow =
    !tenantsLoading &&
    !orgDepsLoading &&
    !!isStreamRegionValid &&
    isCustomerOrg &&
    !hasExistingStreamTenant;

  const showDiscoverAppsSection = connectNotTried || canStreamShow;

  if (!showDiscoverAppsSection) {
    return null;
  }

  return (
    <>
      <Heading size='md' noOfLines={1}>
        Discover apps
      </Heading>
      <Wrap>
        {/* Connect */}
        <WrapItem>
          <TryApp
            productCode={connect as AppType}
            showApp={!!connectNotTried}
            isPendingActivation={isConnectPendingActivation as boolean}
            isLoading={isConnectLoading as boolean}
          />
        </WrapItem>
        <WrapItem>
          <TryApp
            productCode={stream as AppType}
            showApp={canStreamShow}
            isPendingActivation={isStreamPendingActivation as boolean}
            isLoading={isStreamLoading as boolean}
          />
        </WrapItem>
      </Wrap>
    </>
  );
};

export default TryApps;
